import React, { useState, useEffect, useContext } from "react";
import { doc, getDoc } from "firebase/firestore";
import { FaSearch, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { getAuth } from "firebase/auth";
import { AuthContext } from "../../AuthProvider";

// TODO – replace with dynamic value (e.g. user.defaultTeamId)
const TEAM_ID = "zTS5IWYUGpICLRKioMPm";

// helper – normalise to Firestore-safe docId (same as importer script)
const sanitiseEmail = (email) => {
  // The documents in Firestore appear to be stored with the raw email as ID
  // (keeping @ symbols and other characters intact)
  const sanitized = email.trim().toLowerCase();
  console.log(`DEBUG - Sanitizing: "${email}" → "${sanitized}"`);
  return sanitized;
};

const BlacklistChecker = () => {
  const [emailsText, setEmails] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [authReady, setAuthReady] = useState(false);

  // Use AuthContext instead of direct Firebase auth
  const { user, isAuthChecking } = useContext(AuthContext);

  // Debug: Check current user and team data
  useEffect(() => {
    // Wait until authentication check is complete
    if (isAuthChecking) {
      console.log("DEBUG - Auth check still in progress...");
      return;
    }

    setAuthReady(true);

    const fetchDebugInfo = async () => {
      try {
        console.log("DEBUG - Auth state from context:", !!user);
        console.log(
          "DEBUG - User from context:",
          user ? { uid: user.uid, email: user.email } : null
        );

        if (!user) {
          console.log("DEBUG - No authenticated user found!");
          setDebugInfo({
            user: null,
            authStatus: "Not authenticated",
            message: "You must be logged in to use this feature",
          });
          return;
        }

        // Add more detailed email debug info
        console.log(
          "DEBUG - User email (client-side equivalent of request.auth.token.email):",
          user.email
        );

        // Check team document
        const teamRef = doc(db, "teams", TEAM_ID);
        const teamDoc = await getDoc(teamRef);

        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          console.log("DEBUG - Team exists:", true);
          console.log("DEBUG - Team data:", {
            owners: teamData.owners || [],
            creators: teamData.creators || [],
            readers: teamData.readers || [],
          });

          // Check if current user has permissions
          const userHasOwnerAccess =
            user && teamData.owners && teamData.owners.includes(user.uid);
          const userHasCreatorAccess =
            user && teamData.creators && teamData.creators.includes(user.email);
          const userHasReaderAccess =
            user && teamData.readers && teamData.readers.includes(user.email);

          console.log("DEBUG - Access checks:", {
            userHasOwnerAccess,
            userHasCreatorAccess,
            userHasReaderAccess,
            hasAnyAccess:
              userHasOwnerAccess || userHasCreatorAccess || userHasReaderAccess,
          });

          setDebugInfo({
            user: user
              ? {
                  uid: user.uid,
                  email: user.email,
                  emailNote:
                    "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
                }
              : null,
            authStatus: "Authenticated",
            team: {
              id: TEAM_ID,
              exists: true,
              access: {
                isOwner: userHasOwnerAccess,
                isCreator: userHasCreatorAccess,
                isReader: userHasReaderAccess,
                hasAnyAccess:
                  userHasOwnerAccess ||
                  userHasCreatorAccess ||
                  userHasReaderAccess,
              },
            },
          });
        } else {
          console.log("DEBUG - Team doesn't exist!");
          setDebugInfo({
            user: user
              ? {
                  uid: user.uid,
                  email: user.email,
                  emailNote:
                    "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
                }
              : null,
            authStatus: "Authenticated",
            team: { id: TEAM_ID, exists: false },
          });
        }
      } catch (err) {
        console.error("DEBUG - Error fetching debug info:", err);
        setDebugInfo({
          user: user
            ? {
                uid: user.uid,
                email: user.email,
                emailNote:
                  "This client-side user.email is what becomes request.auth.token.email in Firestore rules",
              }
            : null,
          authStatus: user ? "Authenticated" : "Not authenticated",
          error: err.message,
        });
      }
    };

    fetchDebugInfo();
  }, [user, isAuthChecking]);

  // read from Firestore
  const checkEmails = async () => {
    // Ensure authentication is complete
    if (!user) {
      return toast.error("You must be logged in to use this feature");
    }

    let list = emailsText
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (list.length === 0) {
      return toast.warn("Enter at least one e-mail");
    }
    if (list.length > 50) {
      return toast.error("Max 50 emails at once");
    }

    // Double-check auth state right before making the request
    console.log("DEBUG - Auth state at request time (from context):", !!user);
    console.log(
      "DEBUG - User at request time:",
      user
        ? {
            uid: user.uid,
            email: user.email,
            emailNote:
              "This client-side user.email is what Firestore sees as request.auth.token.email",
          }
        : null
    );

    // Try to get the team document again to verify permissions at request time
    try {
      const teamRef = doc(db, "teams", TEAM_ID);
      const teamDoc = await getDoc(teamRef);
      console.log("DEBUG - Team access at request time:", teamDoc.exists());
    } catch (err) {
      console.error("DEBUG - Error checking team at request time:", err);
    }

    setLoading(true);
    try {
      const out = [];
      // simple sequential fetch ( <50 docs – fine for UX )
      for (const raw of list) {
        const id = sanitiseEmail(raw);
        const ref = doc(db, "teams", TEAM_ID, "blacklist", id);

        console.log(`DEBUG - Checking email: "${raw}" → sanitized as: "${id}"`);
        console.log(
          `DEBUG - Document path: "teams/${TEAM_ID}/blacklist/${id}"`
        );
        console.log(`DEBUG - Auth state at fetch time:`, !!user);
        console.log(`DEBUG - User performing lookup:`, {
          uid: user.uid,
          email: user.email,
          emailForRules: `Same as request.auth.token.email in rules`,
        });

        // Add security rules debugging info
        console.log(
          `DEBUG - Security rules checks that should be true for success:`
        );
        console.log(
          `- canSeeTeamDocs(${TEAM_ID}) = isTeamOwner || isTeamCreatorByEmail || isTeamReaderByEmail`
        );
        console.log(
          `- From debug info, isOwner=${debugInfo?.team?.access?.isOwner}, isCreator=${debugInfo?.team?.access?.isCreator}`
        );

        try {
          // Try with the standard reference format first
          console.log(
            `DEBUG - Attempting with standard reference format: doc(db, "teams", TEAM_ID, "blacklist", id)`
          );
          try {
            const snap = await getDoc(ref);
            console.log(`DEBUG - Success! Document exists: ${snap.exists()}`);
            out.push({
              email: raw,
              found: snap.exists(),
            });
            continue; // Skip alternative method if this one worked
          } catch (err) {
            console.error(
              `DEBUG - Standard reference format failed:`,
              err.message
            );
            // Continue to try alternative format
          }

          // Try with string path format as fallback
          console.log(
            `DEBUG - Attempting with string path format: doc(db, \`teams/${TEAM_ID}/blacklist/${id}\`)`
          );
          const directRef = doc(db, `teams/${TEAM_ID}/blacklist/${id}`);
          try {
            const directSnap = await getDoc(directRef);
            console.log(
              `DEBUG - Success with string path! Document exists: ${directSnap.exists()}`
            );
            out.push({
              email: raw,
              found: directSnap.exists(),
            });
          } catch (directErr) {
            console.error(
              `DEBUG - String path format also failed:`,
              directErr.message
            );
            throw directErr; // Re-throw to be caught by outer catch
          }
        } catch (err) {
          console.error(`DEBUG - All attempts failed for "${id}":`, err);
          console.error(`DEBUG - Error type:`, err.constructor.name);
          console.error(`DEBUG - Error code:`, err.code);

          // Check if we can read the team document directly
          try {
            console.log(
              "DEBUG - Trying to read team document directly to verify permissions"
            );
            const teamRef = doc(db, "teams", TEAM_ID);
            const teamDoc = await getDoc(teamRef);
            console.log("DEBUG - Team doc access:", teamDoc.exists());
            if (teamDoc.exists()) {
              const data = teamDoc.data();
              console.log("DEBUG - Team owners:", data.owners);
              console.log("DEBUG - Team creators:", data.creators);
              console.log("DEBUG - Team readers:", data.readers);
            }
          } catch (teamErr) {
            console.error("DEBUG - Team doc access error:", teamErr.message);
          }

          out.push({
            email: raw,
            error: err.message,
          });
        }
      }
      setResults(out);
    } catch (err) {
      console.error("DEBUG - Global error:", err);
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while waiting for auth to be ready
  if (isAuthChecking) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Blacklist Checker</h1>
        <div className="p-4 bg-blue-50 text-blue-700 rounded">
          Loading authentication state...
        </div>
      </div>
    );
  }

  // Show login required message if not authenticated
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Blacklist Checker</h1>
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded">
          You must be logged in to use this feature.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Blacklist Checker</h1>

      {/* Text-area input */}
      <textarea
        value={emailsText}
        onChange={(e) => setEmails(e.target.value)}
        placeholder="Paste up to 50 email addresses – one per line, comma or space-separated"
        rows={6}
        className="w-full p-3 border rounded focus:ring-2 focus:ring-purple-500 mb-4 resize-y"
      />

      <button
        onClick={checkEmails}
        disabled={loading || !authReady}
        className="bg-purple-600 text-white px-6 py-2 rounded flex items-center hover:bg-purple-700 disabled:opacity-50"
      >
        <FaSearch className="mr-2" />
        {loading ? "Checking…" : "Check Emails"}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {results.map(({ email, found, error }) => (
                <tr
                  key={email}
                  className={
                    error ? "bg-yellow-50" : found ? "bg-red-50" : "bg-green-50"
                  }
                >
                  <td className="px-4 py-3 font-mono break-all">{email}</td>
                  <td className="px-4 py-3 flex items-center space-x-2">
                    {error ? (
                      <>
                        <FaTimesCircle className="text-yellow-600" />
                        <span className="text-yellow-600 font-semibold">
                          Error: {error}
                        </span>
                      </>
                    ) : found ? (
                      <>
                        <FaTimesCircle className="text-red-600" />
                        <span className="text-red-600 font-semibold">
                          Black-listed – do NOT use
                        </span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="text-green-600" />
                        <span className="text-green-600 font-semibold">
                          Safe to use
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlacklistChecker;
